import { useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { FiX, FiUpload, FiDownload } from "react-icons/fi";
import { getMetaData } from "../../utils/Constant";
import { Card, Nav, Container, Row, Col } from "react-bootstrap";
import FAQ from "../FAQ/FAQ";

export default function ConvertPngToJpg() {
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [globalTarget, setGlobalTarget] = useState("jpg");
  const [error, setError] = useState("");
  const [activeCategory, setActiveCategory] = useState("jpg-to-png");

  const faqData = {
    "jpg-to-png": {
      title: "JPG to PNG",
      icon: "🖼️",
      color: "primary",
      questions: [
        {
          question: "Why should I convert JPG to PNG?",
          answer:
            "PNG supports transparency (no white background), lossless compression (no quality loss), and is better for graphics/text. JPG is better for photos.",
        },
        {
          question: "Will the quality improve when converting JPG to PNG?",
          answer:
            "No, quality won't improve since JPG is lossy. PNG will maintain the existing quality but prevent further degradation.",
        },
        {
          question:
            "Can I recover lost quality from JPG when converting to PNG?",
          answer:
            "No, JPG compression permanently removes data. PNG keeps current quality but can't restore lost data.",
        },
        {
          question:
            "Will transparent backgrounds appear when converting JPG to PNG?",
          answer:
            "No, JPG doesn't support transparency. The background will remain white/solid.",
        },
      ],
    },
    "png-to-jpg": {
      title: "PNG to JPG",
      icon: "📸",
      color: "success",
      questions: [
        {
          question: "Why convert PNG to JPG?",
          answer:
            "JPG files are much smaller (better for websites, emails), ideal for photographs, and widely supported.",
        },
        {
          question: "How much smaller will my file be?",
          answer:
            "Typically 70-90% smaller! A 1MB PNG might become 100-300KB JPG.",
        },
        {
          question: "Will I lose quality converting PNG to JPG?",
          answer:
            "Yes, JPG uses lossy compression. Some quality loss occurs, but often not noticeable visually.",
        },
        {
          question: "What about transparent backgrounds?",
          answer:
            "JPG doesn't support transparency. Transparent areas will become white.",
        },
      ],
    },
    "png-to-webp": {
      title: "PNG to WebP",
      icon: "🚀",
      color: "info",
      questions: [
        {
          question: "Why convert PNG to WebP?",
          answer:
            "WebP offers 25-35% smaller file sizes than PNG while maintaining transparency and lossless quality.",
        },
        {
          question: "Will transparency be preserved?",
          answer: "Yes! WebP supports transparency just like PNG.",
        },
        {
          question: "Is WebP widely supported?",
          answer:
            "Most modern browsers support WebP (Chrome, Firefox, Edge, Safari 14+). Check your audience's browsers.",
        },
        {
          question: "Any quality loss?",
          answer:
            "With lossless WebP, no quality loss. With lossy WebP, some loss but much better than JPG.",
        },
      ],
    },
    "webp-to-jpg": {
      title: "WebP to JPG",
      icon: "🔄",
      color: "warning",
      questions: [
        {
          question: "Why convert WebP to JPG?",
          answer:
            "For universal compatibility, older software support, or when transparency isn't needed.",
        },
        {
          question: "Will file size change?",
          answer:
            "Usually increases since JPG is less efficient than WebP for same quality.",
        },
        {
          question: "Can I keep transparency?",
          answer:
            "No, JPG doesn't support transparency. Transparent areas become solid (usually white).",
        },
        {
          question: "Any quality concerns?",
          answer:
            "Converting between lossy formats may cause additional quality loss.",
        },
      ],
    },
  };

  const features = [
    {
      icon: "⚡",
      title: "Fast and easy",
      description:
        "Just drop your files on the page, choose an output format and click 'Convert' button. Wait a little for the process to complete.",
    },
    {
      icon: "🔒",
      title: "Security guaranteed",
      description:
        "All image conversion happens directly in your browser. Your files never leave your device, guaranteeing 100% privacy.",
    },
    {
      icon: "📱",
      title: "All devices supported",
      description:
        "Convertible to browser-based and works for all platforms. There is no need to download and install any software.",
    },
  ];

  const supportedTargets = useMemo(() => ["jpg", "jpeg", "png", "webp"], []);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const mapped = files.map((f) => ({
      file: f,
      name: f.name,
      size: f.size,
      target: "jpg",
      urlConverted: null,
      status: "queued",
      progress: 0,
    }));
    setItems(mapped);
    setError("");
  };

  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const updateItemTarget = (idx, target) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, target } : it))
    );
  };

  const metaData = useMemo(() => getMetaData("converter"), []);

  useEffect(() => {
    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, [metaData]);

  const setAllTargets = (target) => {
    setGlobalTarget(target);
    setItems((prev) => prev.map((it) => ({ ...it, target })));
  };

  const convertFileTo = (file, outFmt, updateProgress) =>
    new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onloadstart = () => updateProgress(10);
        reader.onprogress = (e) => {
          if (e.lengthComputable) {
            updateProgress(Math.min(30, (e.loaded / e.total) * 30));
          }
        };
        reader.onload = (ev) => {
          updateProgress(50);
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (["jpg", "jpeg", "webp"].includes(outFmt)) {
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            ctx.drawImage(img, 0, 0);
            let mime = `image/${outFmt === "jpg" ? "jpeg" : outFmt}`;
            const dataUrl = canvas.toDataURL(mime, 1.0);
            updateProgress(100);
            resolve(dataUrl);
          };
          img.onerror = () => reject(new Error("Invalid image"));
          img.src = ev.target.result;
        };
        reader.onerror = () => reject(new Error("Read failed"));
        reader.readAsDataURL(file);
      } catch (e) {
        reject(e);
      }
    });

  const startConvertAll = async () => {
    if (!items.length) return;
    setIsConverting(true);
    setError("");

    const out = [...items];
    for (let i = 0; i < out.length; i++) {
      const target = out[i].target.toLowerCase();
      out[i].status = "converting";
      setItems([...out]);

      const updateProgress = (p) => {
        out[i].progress = p;
        setItems([...out]);
      };

      if (!supportedTargets.includes(target)) {
        out[i].status = "error";
        continue;
      }

      try {
        const dataUrl = await convertFileTo(
          out[i].file,
          target,
          updateProgress
        );
        out[i].urlConverted = dataUrl;
        out[i].status = "done";
      } catch {
        out[i].status = "error";
      }
      setItems([...out]);
    }

    setIsConverting(false);
  };

  const downloadFile = (item) => {
    if (!item.urlConverted) return;
    const a = document.createElement("a");
    a.href = item.urlConverted;
    a.download = item.name.replace(/\.[^.]+$/, "") + `.${item.target}`;
    a.click();
  };

  const downloadAllAsZip = async () => {
    const zip = new JSZip();
    let added = 0;
    items.forEach((it) => {
      if (it.urlConverted) {
        const b64 = it.urlConverted.split(",")[1];
        const outName = it.name.replace(/\.[^.]+$/g, "") + `.${it.target}`;
        zip.file(outName, b64, { base64: true });
        added++;
      }
    });
    if (!added) {
      setError("Nothing to download yet.");
      return;
    }
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, "converted_images.zip");
  };

  return (
    <Container className="py-5">
      {/* Header Section */}
      <Row className="text-center mb-4">
        <Col>
          <h1 className="display-4 fw-bold text-black mb-3">
            Free Online Image Converter
          </h1>
          <p className="lead text-muted">
            Free & fast tools to convert <strong>JPG, PNG, JPEG, WEBP</strong>,
            and more. No signup, no hassle – just quick, secure conversions.
          </p>
        </Col>
      </Row>

      {/* Upload Section */}
      {!items.length && (
        <Row className="justify-content-center mb-5">
          <Col lg={8}>
            <Card className="border-2 border-dashed shadow-sm">
              <Card.Body className="text-center p-5">
                <div
                  className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "80px", height: "80px" }}
                >
                  <span className="fs-1">📤</span>
                </div>
                <h3 className="fw-bold mb-2">Convert Your Files</h3>
                <p className="text-muted mb-4">
                  Drag & drop your files here or click to browse
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-primary btn-lg px-4"
                >
                  <FiUpload className="me-2" /> Choose Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFiles}
                  style={{ display: "none" }}
                />
                <p className="text-muted mt-3 small">Max file size 1GB</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* File List Section */}
      {!!items.length && (
        <Row className="justify-content-center mb-4">
          <Col lg={10}>
            <Card className="shadow-sm">
              <Card.Body className="p-0">
                {items.map((it, idx) => (
                  <div key={idx} className="border-bottom p-3">
                    <Row className="align-items-center">
                      <Col md={6}>
                        <div className="fw-semibold text-truncate">
                          {it.name}
                        </div>
                        <div className="text-muted small">
                          {formatSize(it.size)}
                        </div>

                        {/* Progress Bar */}
                        {it.status === "converting" && (
                          <div className="mt-2">
                            <div
                              className="progress"
                              style={{ height: "6px", width: "160px" }}
                            >
                              <div
                                className="progress-bar"
                                style={{ width: `${it.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                        {it.status === "done" && (
                          <div className="text-success small mt-1">✅ Done</div>
                        )}
                        {it.status === "error" && (
                          <div className="text-danger small mt-1">❌ Error</div>
                        )}
                      </Col>

                      <Col md={6}>
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <select
                            value={it.target}
                            onChange={(e) =>
                              updateItemTarget(idx, e.target.value)
                            }
                            className="form-select w-auto"
                          >
                            {supportedTargets.map((fmt) => (
                              <option key={fmt} value={fmt}>
                                {fmt.toUpperCase()}
                              </option>
                            ))}
                          </select>

                          {it.status === "done" && (
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => downloadFile(it)}
                            >
                              <FiDownload />
                            </button>
                          )}
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeItem(idx)}
                          >
                            <FiX />
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}

                {/* Footer Controls */}
                <div className="bg-light p-3">
                  <Row className="align-items-center">
                    <Col>
                      <div className="d-flex align-items-center gap-2">
                        Convert All to:{" "}
                        <select
                          value={globalTarget}
                          onChange={(e) => setAllTargets(e.target.value)}
                          className="form-select w-auto"
                        >
                          {supportedTargets.map((fmt) => (
                            <option key={fmt} value={fmt}>
                              {fmt.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col className="text-end">
                      <button
                        className="btn btn-primary px-4"
                        onClick={startConvertAll}
                        disabled={isConverting}
                      >
                        {isConverting ? "Converting..." : "Convert →"}
                      </button>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Download All Button */}
      {!isConverting && items.some((i) => i.urlConverted) && (
        <Row className="justify-content-center mb-5">
          <Col className="text-center">
            <button className="btn btn-success px-4" onClick={downloadAllAsZip}>
              <FiDownload className="me-2" /> Download All as ZIP
            </button>
          </Col>
        </Row>
      )}

      {/* Error Message */}
      {error && (
        <Row className="justify-content-center mb-4">
          <Col lg={8}>
            <div className="alert alert-danger text-center">{error}</div>
          </Col>
        </Row>
      )}
      {/* Instructions Section */}
      <Row className="justify-content-center my-5 bg-light">
        <Col lg={10}>
          <Card className="border-0 m-4">
            <Card.Body className="p-4 bg-light">
              <h2 className="display-6 fw-bold text-center mb-4">
                How to Convert Images?
              </h2>
              <ol className="list-group list-group-numbered">
                <li className="list-group-item border-0 bg-transparent">
                  Click the "<strong>Choose Files</strong>" button to upload
                  your files.
                </li>
                <li className="list-group-item border-0 bg-transparent">
                  Select a target image format from the "
                  <strong>Convert To</strong>" drop-down list.
                </li>
                <li className="list-group-item border-0 bg-transparent">
                  Click on the blue "<strong>Convert</strong>" button to start
                  the conversion.
                </li>
              </ol>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Features Grid */}
      <Row className="my-5">
        <Col>
          <Row className="g-4">
            {features.map((feature, index) => (
              <Col key={index} md={6} lg={4}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center p-4">
                    <div
                      className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: "60px", height: "60px" }}
                    >
                      <span className="fs-3">{feature.icon}</span>
                    </div>
                    <h5 className="card-title fw-bold">{feature.title}</h5>
                    <p className="card-text text-muted">
                      {feature.description}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      {/* Quick Tips */}
      <Card className="mt-4 border-0 bg-light">
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-3">💡 Quick Conversion Tips</h5>
          <Row className="g-3">
            {[
              {
                text: "For photos",
                tip: "Use JPG for smallest size",
                icon: "📸",
              },
              {
                text: "For graphics with transparency",
                tip: "Use PNG or WebP",
                icon: "✂️",
              },
              {
                text: "For modern web",
                tip: "WebP offers best of both worlds",
                icon: "🌐",
              },
              {
                text: "For compatibility",
                tip: "JPG and PNG are safest",
                icon: "✅",
              },
            ].map((item, i) => (
              <Col md={6} key={i}>
                <div className="d-flex align-items-start gap-3 p-3 bg-white rounded">
                  <div className="fs-5">{item.icon}</div>
                  <div>
                    <strong className="d-block">{item.text}:</strong>
                    <small className="text-muted">{item.tip}</small>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
      {/* FAQ Section */}
      <Row className="justify-content-center my-5">
        <Col lg={10} xl={9}>
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold text-dark mb-3">
              Image Conversion <span className="text-primary">FAQs</span>
            </h2>
            <p className="lead text-muted">
              Everything you need to know about converting between JPG, PNG, and
              WebP formats.
            </p>
          </div>

          {/* Category Navigation */}
          <Nav className="nav-pills flex-wrap justify-content-center gap-2 mb-5">
            {Object.entries(faqData).map(([key, { title, icon, color }]) => (
              <Nav.Item key={key}>
                <Nav.Link
                  active={activeCategory === key}
                  onClick={() => setActiveCategory(key)}
                  className={`d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-medium ${
                    activeCategory === key ? `bg-${color}` : "text-dark"
                  }`}
                >
                  <span className="fs-5">{icon}</span>
                  {title}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <FAQ
            title={""}
            subTitle={""}
            faqData={faqData[activeCategory].questions}
          />
        </Col>
      </Row>

      {/* Features Section */}
      {/* <Row className="my-5">
        <Col>
          <h3 className="text-center mb-4">Why Choose Our Image Converter?</h3>
          <Row className="g-4">
            {[
              {
                title: "High Quality Conversion",
                description: "Maintain image quality while reducing file size with our advanced conversion algorithms."
              },
              {
                title: "Batch Processing",
                description: "Convert multiple images at once to save time and increase productivity."
              },
              {
                title: "No Registration Required",
                description: "Start converting immediately without creating an account or providing personal information."
              },
              {
                title: "Cross-Platform Compatibility",
                description: "Works perfectly on all devices - desktop, tablet, and mobile phones."
              }
            ].map((feature, index) => (
              <Col key={index} md={6}>
                <div className="d-flex h-100">
                  <div className="flex-shrink-0">
                    <Badge bg="primary" className="rounded-circle p-2 me-3">✓</Badge>
                  </div>
                  <div>
                    <h5>{feature.title}</h5>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </Row> */}
    </Container>
  );
}
